import { useState, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import {
  ChevronDown,
  ChevronRight,
  Calendar,
  Sun,
  BookOpen,
  Palmtree,
  Pencil,
  X,
  Loader2,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { FormField } from '@/shared/components'
import {
  useCalendriersEtudiant,
  usePeriodesEtudiant,
  useCreateCalendrier,
  useUpdateCalendrier,
} from '../hooks/use-calendrier'
import { useEtudiantEvents } from '@/features/events/hooks/use-events'
import type {
  CalendrierEtudiantDto,
  TypePeriode,
} from '@/shared/types/calendrier'

// ── Helpers ────────────────────────────────────────────────

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })
}

function daysBetween(start: string, end: string) {
  const ms = new Date(end).getTime() - new Date(start).getTime()
  return Math.round(ms / 86400000) + 1
}

function generateAnneeScolaireOptions(): string[] {
  const now = new Date()
  // L'année scolaire commence en septembre : avant septembre, on est encore sur l'année précédente
  const startYear = now.getMonth() < 8 ? now.getFullYear() - 1 : now.getFullYear()
  return Array.from({ length: 5 }, (_, i) => `${startYear + i}-${startYear + i + 1}`)
}

interface SectionConfig {
  key: TypePeriode
  label: string
  icon: React.ComponentType<{ className?: string }>
  color: string
  bgColor: string
  borderColor: string
  selectedBg: string
}

const SECTION_CONFIG: SectionConfig[] = [
  {
    key: 'VACANCES',
    label: 'Vacances',
    icon: Palmtree,
    color: 'text-orange-600',
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-200',
    selectedBg: 'bg-orange-50/50',
  },
  {
    key: 'SEMAINE_A',
    label: 'Semaine A',
    icon: BookOpen,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    selectedBg: 'bg-blue-50/50',
  },
  {
    key: 'SEMAINE_B',
    label: 'Semaine B',
    icon: BookOpen,
    color: 'text-red-600',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
    selectedBg: 'bg-red-50/50',
  },
  {
    key: 'RELAX',
    label: 'Relax / Jours fériés',
    icon: Sun,
    color: 'text-violet-600',
    bgColor: 'bg-violet-50',
    borderColor: 'border-violet-200',
    selectedBg: 'bg-violet-50/50',
  },
]

// For non-AB calendars, we show ECOLE instead of SEMAINE_A/B
const SECTION_CONFIG_ECOLE: SectionConfig[] = [
  SECTION_CONFIG[0], // VACANCES
  {
    key: 'ECOLE',
    label: 'Période scolaire',
    icon: BookOpen,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    selectedBg: 'bg-blue-50/50',
  },
  SECTION_CONFIG[3], // RELAX
]

// ── Shared styles ──────────────────────────────────────────

const inputClass =
  'h-10 w-full rounded-lg border border-neutral-300 bg-neutral-50 px-3 text-sm transition-colors hover:border-neutral-400 focus:border-primary focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/25'

const readOnlyClass =
  'h-10 w-full rounded-lg border border-neutral-200 bg-neutral-100 px-3 text-sm text-neutral-500 cursor-not-allowed'

const selectClass =
  'h-10 w-full rounded-lg border border-neutral-300 bg-neutral-50 px-3 text-sm transition-colors hover:border-neutral-400 focus:border-primary focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/25'

// ── DateRow ────────────────────────────────────────────────

interface DateRowItem {
  id: number | string
  dateDebut: string
  dateFin: string
  libelle: string | null
  isStudentEvent?: boolean
}

function DateRow({ d }: { d: DateRowItem }) {
  const isSingle = d.dateDebut === d.dateFin
  const jours = daysBetween(d.dateDebut, d.dateFin)
  return (
    <div className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors hover:bg-neutral-50">
      <Calendar className="h-4 w-4 shrink-0 text-neutral-400" />
      <span className="min-w-0 flex-1 font-medium text-neutral-700">
        {d.libelle || 'Sans libellé'}
      </span>
      {d.isStudentEvent && (
        <span className="shrink-0 rounded-full bg-violet-100 px-2 py-0.5 text-xs font-medium text-violet-600">
          étudiant
        </span>
      )}
      <span className="shrink-0 text-neutral-500">
        {isSingle ? formatDate(d.dateDebut) : `${formatDate(d.dateDebut)} → ${formatDate(d.dateFin)}`}
      </span>
      <span className="shrink-0 rounded-full bg-neutral-100 px-2 py-0.5 text-xs font-medium text-neutral-600">
        {jours}j
      </span>
    </div>
  )
}

// ── Create calendar form ───────────────────────────────────

const createSchema = z.object({
  titre: z.string().min(1, 'Le titre est obligatoire'),
  anneeScolaire: z.string().min(1, 'L\'année scolaire est obligatoire'),
  zone: z.string().min(1, 'La zone est obligatoire'),
  semainesAB: z.boolean(),
})

type CreateFormValues = z.infer<typeof createSchema>

function CalendrierCreateForm({
  etudiantId,
  isPending,
  onSubmit,
}: {
  etudiantId: number
  isPending: boolean
  onSubmit: (data: CreateFormValues) => void
}) {
  const { t } = useTranslation()
  const annees = generateAnneeScolaireOptions()

  const { register, handleSubmit, setValue, watch, formState: { errors } } = useForm<CreateFormValues>({
    resolver: zodResolver(createSchema),
    defaultValues: {
      titre: `Calendrier ${annees[0]}`,
      anneeScolaire: annees[0],
      zone: 'Zone B',
      semainesAB: true,
    },
  })

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="rounded-xl border border-neutral-200 bg-white shadow-sm"
    >
      <div className="border-b border-neutral-100 px-6 py-4">
        <h2 className="text-base font-semibold text-primary">
          {t('student.calendar.createTitle', 'Créer un calendrier')}
        </h2>
        <p className="mt-1 text-sm text-neutral-500">
          {t(
            'student.calendar.createDescription',
            'Le calendrier sera généré automatiquement avec les vacances, semaines et jours fériés.',
          )}
        </p>
      </div>
      <div className="space-y-5 p-6">
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <FormField
            label={t('student.calendar.title', 'Titre')}
            error={errors.titre?.message}
            required
          >
            <input {...register('titre')} className={inputClass} />
          </FormField>
          <FormField
            label={t('student.calendar.schoolYear', 'Année scolaire')}
            error={errors.anneeScolaire?.message}
            required
          >
            <select {...register('anneeScolaire')} className={selectClass}>
              {annees.map((a) => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
          </FormField>
          <FormField
            label={t('student.calendar.zone', 'Zone')}
            error={errors.zone?.message}
            required
          >
            <select {...register('zone')} className={selectClass}>
              <option value="Zone A">Zone A</option>
              <option value="Zone B">Zone B</option>
              <option value="Zone C">Zone C</option>
            </select>
          </FormField>
          <div className="flex items-end pb-1">
            <label className="flex cursor-pointer items-center gap-3">
              <div className="relative">
                <input
                  type="checkbox"
                  checked={watch('semainesAB')}
                  onChange={(e) => setValue('semainesAB', e.target.checked)}
                  className="peer sr-only"
                />
                <div className="h-6 w-11 rounded-full bg-neutral-300 transition-colors peer-checked:bg-primary" />
                <div className="absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform peer-checked:translate-x-5" />
              </div>
              <span className="text-sm font-medium text-neutral-700">
                {t('student.calendar.alternatingWeeks', 'Alternance A/B')}
              </span>
            </label>
          </div>
        </div>
        <div className="flex justify-end border-t border-neutral-100 pt-4">
          <button
            type="submit"
            disabled={isPending}
            className="rounded-lg bg-primary px-6 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary/90 disabled:opacity-50"
          >
            {isPending
              ? t('student.calendar.creating', 'Création...')
              : t('student.calendar.create', 'Créer le calendrier')}
          </button>
        </div>
      </div>
    </form>
  )
}

// ── Edit calendar form (inline) ────────────────────────────

function CalendrierEditForm({
  calendrier,
  isPending,
  onSubmit,
  onCancel,
}: {
  calendrier: CalendrierEtudiantDto
  isPending: boolean
  onSubmit: (titre: string) => void
  onCancel: () => void
}) {
  const { t } = useTranslation()
  const [titre, setTitre] = useState(calendrier.titre)

  return (
    <div className="rounded-xl border-2 border-primary/30 bg-white shadow-sm">
      <div className="flex items-center justify-between border-b border-neutral-100 px-6 py-4">
        <h2 className="text-base font-semibold text-primary">
          {t('student.calendar.editTitle', 'Modifier le calendrier')}
        </h2>
        <button
          onClick={onCancel}
          className="rounded-md p-1.5 text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-600"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="space-y-5 p-6">
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <FormField label={t('student.calendar.title', 'Titre')}>
            <input
              value={titre}
              onChange={(e) => setTitre(e.target.value)}
              className={inputClass}
            />
          </FormField>
          <FormField label={t('student.calendar.schoolYear', 'Année scolaire')}>
            <input value={calendrier.anneeScolaire} readOnly className={readOnlyClass} />
          </FormField>
          <FormField label={t('student.calendar.zone', 'Zone')}>
            <input value={calendrier.zone} readOnly className={readOnlyClass} />
          </FormField>
          <FormField label={t('student.calendar.alternatingWeeks', 'Alternance')}>
            <input
              value={calendrier.semainesAB ? 'Semaines A/B' : 'Non'}
              readOnly
              className={readOnlyClass}
            />
          </FormField>
        </div>
        <div className="flex items-center justify-end gap-3 border-t border-neutral-100 pt-4">
          <button
            onClick={onCancel}
            className="rounded-lg border border-neutral-300 px-5 py-2.5 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50"
          >
            {t('common.cancel', 'Annuler')}
          </button>
          <button
            onClick={() => onSubmit(titre)}
            disabled={isPending || !titre.trim()}
            className="rounded-lg bg-primary px-6 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary/90 disabled:opacity-50"
          >
            {isPending
              ? t('student.calendar.saving', 'Enregistrement...')
              : t('student.calendar.save', 'Enregistrer')}
          </button>
        </div>
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════

interface CalendrierTabProps {
  etudiantId: number
  onAnneeScolaireLoaded?: (anneeScolaire: string) => void
}

export function CalendrierTab({ etudiantId, onAnneeScolaireLoaded }: CalendrierTabProps) {
  const { t } = useTranslation()

  // ── Data loading ───────────────────────────────────────
  const { data: calendriers, isLoading: loadingCalendriers } = useCalendriersEtudiant(etudiantId)
  const { data: periodes, isLoading: loadingPeriodes } = usePeriodesEtudiant(etudiantId)
  const { data: studentEvents } = useEtudiantEvents(etudiantId)

  // ── Mutations ──────────────────────────────────────────
  const createCalendrier = useCreateCalendrier()
  const updateCalendrier = useUpdateCalendrier()

  // ── Local state ────────────────────────────────────────
  const [editing, setEditing] = useState(false)
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({})

  // Pick first calendar (one per student per year typically)
  const calendrier = calendriers?.[0] ?? null

  // Notify parent of anneeScolaire for the tab label
  const anneeScolaire = calendrier?.anneeScolaire
  useState(() => {
    if (anneeScolaire && onAnneeScolaireLoaded) {
      onAnneeScolaireLoaded(anneeScolaire)
    }
  })
  // Also update when data changes
  if (anneeScolaire && onAnneeScolaireLoaded) {
    onAnneeScolaireLoaded(anneeScolaire)
  }

  // ── Build sections from periodes + student events ──────
  const sections = useMemo(() => {
    if (!periodes) return []
    const config = calendrier?.semainesAB ? SECTION_CONFIG : SECTION_CONFIG_ECOLE
    return config.map((cfg) => {
      const matchingPeriodes = periodes.filter((p) => p.typePeriode === cfg.key)
      const dates: DateRowItem[] = matchingPeriodes.flatMap((p) =>
        (p.datesPeriodes ?? []).map((d) => ({
          id: d.id,
          dateDebut: d.dateDebut,
          dateFin: d.dateFin,
          libelle: d.libelle,
        })),
      )
      // Merge student RELAX events into the RELAX section
      if (cfg.key === 'RELAX' && studentEvents) {
        const relaxEvents = studentEvents.filter((e) => e.type === 'RELAX')
        for (const ev of relaxEvents) {
          dates.push({
            id: `ev-${ev.id}`,
            dateDebut: ev.dateDebut,
            dateFin: ev.dateFin || ev.dateDebut,
            libelle: ev.libelle,
            isStudentEvent: true,
          })
        }
      }
      // Sort dates chronologically
      dates.sort((a, b) => a.dateDebut.localeCompare(b.dateDebut))
      return { ...cfg, dates, count: dates.length }
    })
  }, [periodes, calendrier?.semainesAB, studentEvents])

  // ── Filters (all active by default) ────────────────────
  const allKeys = useMemo(() => sections.map((s) => s.key), [sections])
  const [activeFilters, setActiveFilters] = useState<Set<string>>(new Set())

  // Reset filters when sections change
  const effectiveFilters = activeFilters.size === 0 || activeFilters.size === allKeys.length
    ? new Set(allKeys)
    : activeFilters

  const allSelected = effectiveFilters.size === allKeys.length

  const toggleFilter = (key: string) => {
    setActiveFilters((prev) => {
      const next = new Set(prev.size === 0 ? allKeys : prev)
      if (next.has(key)) {
        next.delete(key)
      } else {
        next.add(key)
      }
      // If all re-selected, reset to empty (= show all)
      if (next.size === allKeys.length) return new Set()
      return next
    })
  }

  const toggleAccordion = (key: string) =>
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }))

  const visibleSections = sections.filter((s) => effectiveFilters.has(s.key))

  // ── Handlers ───────────────────────────────────────────

  const handleCreate = (data: CreateFormValues) => {
    createCalendrier.mutate({
      etudiantId,
      titre: data.titre,
      anneeScolaire: data.anneeScolaire,
      zone: data.zone,
      semainesAB: data.semainesAB,
    })
  }

  const handleUpdate = (titre: string) => {
    if (!calendrier) return
    updateCalendrier.mutate(
      { id: calendrier.id, data: { titre } },
      { onSuccess: () => setEditing(false) },
    )
  }

  // ── Loading state ──────────────────────────────────────

  if (loadingCalendriers || loadingPeriodes) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  // ── No calendar yet → create form ─────────────────────

  if (!calendrier) {
    return (
      <CalendrierCreateForm
        etudiantId={etudiantId}
        isPending={createCalendrier.isPending}
        onSubmit={handleCreate}
      />
    )
  }

  // ── Calendar exists → display ─────────────────────────

  return (
    <div className="space-y-4">
      {/* Info bar / Edit form */}
      {editing ? (
        <CalendrierEditForm
          calendrier={calendrier}
          isPending={updateCalendrier.isPending}
          onSubmit={handleUpdate}
          onCancel={() => setEditing(false)}
        />
      ) : (
        <div className="flex items-center justify-between rounded-xl border border-neutral-200 bg-white px-6 py-4 shadow-sm">
          <div className="flex items-center gap-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
              <Calendar className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-neutral-900">
                {calendrier.anneeScolaire}
              </h2>
              <p className="text-sm text-neutral-500">
                {calendrier.zone} · {calendrier.semainesAB
                  ? t('student.calendar.alternatingAB', 'Alternance semaines A/B')
                  : t('student.calendar.noAlternating', 'Sans alternance')}
              </p>
            </div>
          </div>
          <button
            onClick={() => setEditing(true)}
            className="flex items-center gap-2 rounded-lg border border-neutral-300 px-4 py-2 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50"
          >
            <Pencil className="h-3.5 w-3.5" />
            {t('student.calendar.edit', 'Modifier')}
          </button>
        </div>
      )}

      {/* Clickable filter counters */}
      <div className={cn('grid gap-3', sections.length === 4 ? 'grid-cols-4' : 'grid-cols-3')}>
        {sections.map((section) => {
          const Icon = section.icon
          const isActive = effectiveFilters.has(section.key)
          return (
            <button
              key={section.key}
              onClick={() => toggleFilter(section.key)}
              className={cn(
                'flex items-center gap-3 rounded-xl border-2 bg-white px-4 py-3 transition-all',
                isActive
                  ? `${section.borderColor} ${section.selectedBg} shadow-md`
                  : 'border-neutral-200 opacity-50 hover:opacity-75',
              )}
            >
              <div className={cn('flex h-8 w-8 items-center justify-center rounded-lg', section.bgColor)}>
                <Icon className={cn('h-4 w-4', section.color)} />
              </div>
              <div className="text-left">
                <p className="text-lg font-bold text-neutral-900">{section.count}</p>
                <p className="text-xs text-neutral-500">{section.label}</p>
              </div>
            </button>
          )
        })}
      </div>

      {/* Active filter hint */}
      {!allSelected && (
        <div className="flex items-center gap-2">
          <span className="text-xs text-neutral-400">
            {t('student.calendar.activeFilters', 'Filtres actifs')} :{' '}
            {visibleSections.map((s) => s.label).join(', ')}
          </span>
          <button
            onClick={() => setActiveFilters(new Set())}
            className="text-xs font-medium text-primary hover:underline"
          >
            {t('student.calendar.showAll', 'Tout afficher')}
          </button>
        </div>
      )}

      {/* Accordions */}
      <div className="rounded-xl border border-neutral-200 bg-white shadow-sm">
        <div className="border-b border-neutral-100 px-6 py-4">
          <h2 className="text-base font-semibold text-primary">
            {t('student.calendar.datesTitle', 'Dates du calendrier')}
          </h2>
        </div>
        <div className="divide-y divide-neutral-100">
          {visibleSections.map((section) => {
            const isOpen = openSections[section.key] ?? false
            const Icon = section.icon
            return (
              <div key={section.key}>
                <button
                  onClick={() => toggleAccordion(section.key)}
                  className="flex w-full items-center gap-3 px-6 py-3.5 text-left transition-colors hover:bg-neutral-50"
                >
                  {isOpen ? (
                    <ChevronDown className="h-4 w-4 text-neutral-400" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-neutral-400" />
                  )}
                  <div className={cn('flex h-7 w-7 items-center justify-center rounded-md', section.bgColor)}>
                    <Icon className={cn('h-3.5 w-3.5', section.color)} />
                  </div>
                  <span className="font-medium text-neutral-800">{section.label}</span>
                  <span className="text-sm text-neutral-400">({section.count})</span>
                </button>
                {isOpen && (
                  <div className="space-y-1 px-6 pb-4">
                    {section.dates.length === 0 ? (
                      <p className="py-2 text-center text-sm text-neutral-400">
                        {t('student.calendar.noDates', 'Aucune date')}
                      </p>
                    ) : (
                      section.dates.map((d) => <DateRow key={d.id} d={d} />)
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
